import {SVG} from '../../../../../constants/assetLists';
import {TextField} from '@mui/material';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import moment from 'moment';
import AppMenu from '../../../../../components/common/AppMenu';
import AppButton from '../../../../../components/common/AppButton';
import AppMUIMenuItem from '../../../../../components/common/AppMUIMenuItem';
import useScreenSize from '../../../../../hooks/useScreenSize';
import {DayPicker, useDayPicker} from 'react-day-picker';
import {he} from 'date-fns/locale';
import {useReactToPrint} from 'react-to-print';
import AppDialog from '../../../../../components/common/AppDialog';
import 'react-day-picker/dist/style.css';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';

import {getDateInUTC, getReservationInfo, isNowInShift} from '../../../../../utils';
import {useNavigate} from 'react-router';

/* ── Custom DayPicker Caption with Year/Month picker overlay ── */

const MONTH_NAMES_HE = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];
const START_YEAR = 2020;
const END_YEAR = new Date().getFullYear() + 10;

function MonthCaption({displayMonth}: {displayMonth: Date}) {
  const {goToMonth} = useDayPicker();
  const [view, setView] = useState<'none' | 'year' | 'month'>('none');
  const [pendingYear, setPendingYear] = useState<number>(
    displayMonth.getFullYear(),
  );
  const yearScrollRef = useRef<HTMLDivElement>(null);

  // Scroll selected year into view when year picker opens
  useEffect(() => {
    if (view === 'year' && yearScrollRef.current) {
      const selected = yearScrollRef.current.querySelector(
        '[data-selected="true"]',
      ) as HTMLElement | null;
      selected?.scrollIntoView({block: 'center'});
    }
  }, [view]);

  const handleYearClick = (year: number) => {
    setPendingYear(year);
    setView('month');
  };

  const handleMonthClick = (monthIndex: number) => {
    goToMonth(new Date(pendingYear, monthIndex));
    setView('none');
  };

  const labelText = `${MONTH_NAMES_HE[displayMonth.getMonth()]} ${displayMonth.getFullYear()}`;

  return (
    <div className="relative flex justify-between items-center w-full px-4">
      {/* Previous Month Button (on the right in RTL) */}
      <button
        type="button"
        onClick={() =>
          goToMonth(moment(displayMonth).subtract(1, 'month').toDate())
        }
        className="p-2 hover:opacity-50 transition-opacity cursor-pointer flex items-center justify-center">
        <img
          src={SVG.greaterThanGrayIcon}
          alt="previous month"
          className="w-3 h-3 object-contain"
        />
      </button>

      {/* Clickable Month+Year label */}
      <button
        onClick={() => setView(v => (v === 'none' ? 'year' : 'none'))}
        className="flex items-center gap-1 text-xl font-medium text-gray-800 hover:text-[#0080A6] transition-colors cursor-pointer select-none">
        {labelText}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: view !== 'none' ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Next Month Button (on the left in RTL) */}
      <button
        type="button"
        onClick={() => goToMonth(moment(displayMonth).add(1, 'month').toDate())}
        className="p-2 hover:opacity-50 transition-opacity cursor-pointer flex items-center justify-center">
        <img
          src={SVG.lessThanGrayIcon}
          alt="next month"
          className="w-3 h-3 object-contain"
        />
      </button>

      {/* Overlay panel */}
      {view !== 'none' && (
        <div
          style={{
            position: 'absolute',
            top: '36px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50,
            width: '260px',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: '12px',
          }}>
          {/* ── YEAR VIEW ── */}
          {view === 'year' && (
            <>
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: '12px',
                  color: '#6b7280',
                  textAlign: 'center',
                }}>
                בחר שנה
              </p>
              <div
                ref={yearScrollRef}
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  justifyContent: 'center',
                }}>
                {Array.from(
                  {length: END_YEAR - START_YEAR + 1},
                  (_, i) => START_YEAR + i,
                ).map(year => {
                  const isSel = year === displayMonth.getFullYear();
                  return (
                    <button
                      key={year}
                      data-selected={isSel}
                      onClick={() => handleYearClick(year)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: '8px',
                        border: isSel
                          ? '2px solid #0080A6'
                          : '1.5px solid #e5e7eb',
                        background: isSel ? '#0080A6' : '#fff',
                        color: isSel ? '#fff' : '#374151',
                        fontWeight: isSel ? 700 : 400,
                        fontSize: '13px',
                        cursor: 'pointer',
                        minWidth: '52px',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={e => {
                        if (!isSel)
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = '#f0fffe';
                      }}
                      onMouseLeave={e => {
                        if (!isSel)
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = '#fff';
                      }}>
                      {year}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ── MONTH VIEW ── */}
          {view === 'month' && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px',
                }}>
                <button
                  onClick={() => setView('year')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#038D92',
                    fontSize: '18px',
                    lineHeight: 1,
                    padding: '0 4px',
                  }}>
                  ‹
                </button>
                <p
                  style={{
                    margin: 0,
                    flex: 1,
                    fontSize: '12px',
                    color: '#6b7280',
                    textAlign: 'center',
                  }}>
                  בחר חודש — {pendingYear}
                </p>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '6px',
                }}>
                {MONTH_NAMES_HE.map((name, idx) => {
                  const isSel =
                    idx === displayMonth.getMonth() &&
                    pendingYear === displayMonth.getFullYear();
                  return (
                    <button
                      key={idx}
                      onClick={() => handleMonthClick(idx)}
                      style={{
                        padding: '7px 4px',
                        borderRadius: '8px',
                        border: isSel
                          ? '2px solid #0080A6'
                          : '1.5px solid #e5e7eb',
                        background: isSel ? '#0080A6' : '#fff',
                        color: isSel ? '#fff' : '#374151',
                        fontWeight: isSel ? 700 : 400,
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={e => {
                        if (!isSel)
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = '#f0fffe';
                      }}
                      onMouseLeave={e => {
                        if (!isSel)
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = '#fff';
                      }}>
                      {name}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Print helper components ── */

const PrintField = ({
  label,
  value,
  borderLeft,
}: {
  label: string;
  value: React.ReactNode;
  borderLeft?: boolean;
}) => (
  <div className={`px-3.5 py-2 ${borderLeft ? 'border-l border-gray90' : ''}`}>
    <div className="text-[11px] text-gray mb-0.5">{label}</div>
    <div className="text-[13px]">{value}</div>
  </div>
);

const PrintSectionLabel = ({label, count}: {label: string; count?: number}) => (
  <div className="text-[11px] text-gray mb-1.5">
    {label}
    {count !== undefined ? ` (${count})` : ''}
  </div>
);

const PrintDetailTable = ({
  headers,
  rows,
}: {
  headers: string[];
  rows: React.ReactNode[][];
}) => (
  <table
    className="w-full border-collapse text-xs"
    style={{tableLayout: 'fixed'}}>
    <thead>
      <tr>
        {headers.map((h, i) => (
          <th
            key={i}
            className="text-right px-1.5 py-1 border-b border-gray80 text-gray font-medium"
            style={i === 0 ? {width: '32px'} : undefined}>
            {h}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows?.map((cells, ri) => (
        <tr key={ri}>
          {cells.map((cell, ci) => (
            <td
              key={ci}
              className="text-right px-1.5 py-1 border-b border-gray95">
              {cell}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

function Header() {
  const {
    expandDailyBriefSections,
    setExpandDailyBriefSections,
    briefSelectedDate,
    setBriefSelectedDate,
    shiftTimeList,
    selectedShift,
    setSelectedShift,
    briefData,
    setOpenAddEditAnnouncementDialog,
    dailyBriefGroupBy,
    setDailyBriefGroupBy,
  } = useOperationsContext();

  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = React.useState(new Date());

  const screenSize = useScreenSize();
  const isLarge = screenSize == 'xl' || screenSize == '2xl';

  const formattedShifts = useMemo(() => {
    const shifts = shiftTimeList?.shifts ?? [];

    if (shifts?.length === 0) return [];

    const sorted = [...shifts].sort((a: any, b: any) => {
      const startCmp = a.timing.startTime.localeCompare(b.timing.startTime);
      if (startCmp !== 0) return startCmp;
      return a.timing.endTime.localeCompare(b.timing.endTime);
    });

    const mapped = sorted.map(
      (item: {
        timing: {startTime: string; endTime: string};
        name: string;
        _id: string;
      }) => ({
        label: `${item.name} (${item.timing.startTime}-${item.timing.endTime})`,
        value: item._id,
      }),
    );

    return [{label: 'כל המשמרות', value: 'all'}, ...mapped];
  }, [shiftTimeList]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [showCalendar, setShowCalendar] = React.useState(false);
  // const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [showPrintConfirmationDialog, setShowPrintConfirmationDialog] =
    useState(false);
  const printRef = React.useRef(null);
  const MENUITEMS = [
    {
      title: "תצוגה לפי מחלקה",
      value: "department",
    },
    {
      title: "תצוגה לפי סוג",
      value: "announcementType",
    },
    {
      title: "בחירת יום",
      value: "calendar",
      icon: SVG.calendarGrayIcon,
    },
    {
      title: "הדפסת בריף",
      value: "print",
      icon: SVG.printerGrayIcon,
    },
    {
      title: "שיתוף בריף",
      value: "share",
      icon: SVG.shareGrayIcon,
    },
    {
      title: expandDailyBriefSections?.length == 0 ? "כווץ הכל" : "הרחב הכל",
      value: "expand",
      icon:
        expandDailyBriefSections?.length == 0
          ? SVG.collapseAllIcon
          : SVG.expandGrayIcon,
    },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (type: string) => {
    setBriefSelectedDate(prev => {
      if (type == 'today') return getDateInUTC(new Date());
      else if (type == 'prev')
        return getDateInUTC(new Date(prev.setDate(prev.getDate() - 1)));
      else return getDateInUTC(new Date(prev.setDate(prev.getDate() + 1)));
    });
  };

  const handleMenuChange = (item: string) => {
    switch (item) {
      case 'print':
        setShowPrintConfirmationDialog(true);
        break;
      case 'calendar':
        setShowCalendar(true);
        handleMenuClose();
        break;
      case 'expand':
        setExpandDailyBriefSections(prev => {
          if (prev.length > 0) {
            return [];
          } else {
            return briefData.map((brief: any) => {
              const rt = brief?.group?.name
              return rt
            });
          }
        });
        break;
      case 'department':
        setDailyBriefGroupBy('department');
        handleMenuClose();
        break;
      case 'announcementType':
        setDailyBriefGroupBy('announcementType');
        handleMenuClose();
        break;
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Brief',
  });
  const handleOnConfirm = () => {
    setShowPrintConfirmationDialog(false);
    handlePrint();
  };

  const handleConfirmationClose = () => {
    setShowPrintConfirmationDialog(false);
  };

  useEffect(() => {
    if (!shiftTimeList?.shifts?.length) return;

    const updateShift = () => {
      const nowStr = moment().format('HH:mm');

      const sorted = [...shiftTimeList.shifts].sort((a: any, b: any) => {
        const startA = a.timing.startTime.localeCompare(b.timing.startTime);
        if (startA !== 0) return startA;
        return a.timing.endTime.localeCompare(b.timing.endTime);
      });

      const currentShift = sorted.find(s =>
        isNowInShift(s.timing.startTime, s.timing.endTime, nowStr),
      );

      setSelectedShift(currentShift?._id ?? sorted[0]?._id);
    };

    updateShift();
    const interval = setInterval(updateShift, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval); // Cleanup on unmount
  }, [shiftTimeList]);

  useEffect(() => {
    setSelectedDate(briefSelectedDate);
  }, [briefSelectedDate]);
  // PRINT DATA — flatten brief announcements into detail rows
   const rowsToPrint = useMemo(
     () =>
       briefData?.flatMap((item: any) =>
         item?.announcements?.map((a: any) => {
          const reservationInfo = getReservationInfo(a);
          const title = reservationInfo?.label
            ? `${reservationInfo.time} ${reservationInfo.label}`
            : (a?.announcementTitle ?? "לא ידוע");
          return {
            title,
            description: a?.description || "—",
            assignees:
              a?.assignee?.map((u: any) => ({
                name:
                  `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ||
                  "לא ידוע",
                role: u?.roles?.[0]?.roleType
                  ? typeof u.roles[0].roleType === "string"
                    ? u.roles[0].roleType
                    : (u.roles[0].roleType?.he ??
                      u.roles[0].roleType?.name?.he ??
                      "")
                  : "—",
              })) ?? [],
            roleType:
              (typeof item?.roleTypes?.[0] === "string"
                ? item?.roleTypes?.[0]
                : item?.roleTypes?.[0]?.he) ?? "לא ידוע",
            createdAt: a?.createdAt
              ? moment(a.createdAt).format("DD/MM/YYYY HH:mm")
              : "—",
            respondedCount: a?.respondedCount ?? 0,
            tasks:
              a?.tasks?.map((t: any) => ({
                title: t.taskTitle ?? t.title ?? "ללא כותרת",
                recipients:
                  t?.recipients?.length > 0
                    ? t.recipients
                        .map((u: any) =>
                          `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim(),
                        )
                        .join(", ")
                    : "לא הוקצה",
                isCompleted: !!t?.isCompleted,
              })) ?? [],
          };
         }),
       ) ?? [],
     [briefData],
   );


  const handleQuickTypeNavigation = () => {
    navigate('/operations', {state: {initialType: 'Quick'}});
    setOpenAddEditAnnouncementDialog(true);
    handleMenuClose();
  };

  return (
    <div
      className={`${
        isLarge && 'rounded-tl-[32px] rounded-tr-[32px]'
      } bg-white p-3 shadow-md z-[2]`}>
      <div className="flex flex-col gap-3">
        <p className="m-0 text-[24px] leading-6 font-light text-black">
          בריף יומי
        </p>

        <div className="flex justify-between items-center gap-2">
          <TextField
            variant="standard"
            sx={{
              width: '259px',
            }}
            slotProps={{
              input: {
                style: {
                  fontSize: '18px',
                  color: '#038D92',
                  fontWeight: 500,
                  borderWidth: '0',
                },
              },
              select: {
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: '400px',
                      border: '1px solid #707070',
                      boxShadow: '0 4px 4px #00000040',
                      '& .MuiMenuItem-root:hover': {
                        backgroundColor: '#D8FEFF',
                      },
                      '& .MuiMenuItem-root.Mui-selected:hover': {
                        backgroundColor: '#D8FEFF',
                      },
                    },
                  },
                },
              },
            }}
            select
            value={selectedShift}
            onChange={e => setSelectedShift(e.target.value)}>
            {formattedShifts.map((option: {label: string; value: string}) => (
              <AppMUIMenuItem key={option.value} value={option.value}>
                {option.label}
              </AppMUIMenuItem>
            ))}
          </TextField>

          <button
            className="w-[44px] h-[44px] flex justify-center items-center rounded-full border-[2px] border-tealBlue bg-aqua hover:bg-softAqua  transition-all duration-200"
            onClick={handleQuickTypeNavigation}>
            <img
              src={SVG.quickTypeIcon}
              alt="quick"
              className="w-5 h-[21.5px] object-contain"
            />
          </button>
        </div>

        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-5">
            <AppButton
              className="border-2"
              onClick={() => handleDateSelect('today')}>
              {moment(selectedDate).format('DD/MM/YYYY') ===
              moment().format('DD/MM/YYYY')
                ? `היום, ${moment(selectedDate).format('ddd DD/MM')}`
                : moment(selectedDate).format('ddd DD/MM')}
            </AppButton>

            <div className="flex items-center gap-10 ">
              <button
                className="w-[12px] h-[12px] hover:opacity-40"
                onClick={() => handleDateSelect('prev')}>
                <img
                  src={SVG.greaterThanGrayIcon}
                  alt="add"
                  className="w-full h-full object-contain"
                />
              </button>

              <button
                className="w-[12px] h-[12px] hover:opacity-40"
                onClick={() => handleDateSelect('next')}>
                <img
                  src={SVG.lessThanGrayIcon}
                  alt="add"
                  className="w-full h-full object-contain"
                />
              </button>
            </div>
            {/* ── Print Layout (hidden, rendered for react-to-print) ── */}
            <div className="hidden">
              <div
                ref={printRef}
                dir="rtl"
                className="p-6 font-rubik text-sm text-right">
                <h2 className="m-0 mb-1 text-xl">בריף יומי</h2>
                <div className="mb-4 text-gray text-sm">
                  {moment(briefSelectedDate).format('DD/MM/YYYY')}
                </div>

                {rowsToPrint.length === 0 ? (
                  <p className="text-center text-gray50 py-8">
                    אין נתונים להצגה
                  </p>
                ) : (
                  rowsToPrint.map((row: any, idx: number) => (
                    <div
                      key={idx}
                      className="border border-gray80 rounded-lg mb-4 overflow-hidden"
                      style={{pageBreakInside: 'avoid'}}>
                      {/* Title bar */}
                      <div className="bg-gray95 px-3.5 py-2.5 border-b border-gray80 text-[15px] font-semibold">
                        {row?.title}
                      </div>

                      {/* Role Type + Respondent Count */}
                      <div className="grid grid-cols-2 border-b border-gray90">
                        <PrintField
                          label="תפקיד"
                          value={row?.roleType}
                          borderLeft
                        />
                        <PrintField
                          label="מספר מגיבים"
                          value={row?.respondedCount}
                        />
                      </div>

                      {/* Created At */}
                      <div className="border-b border-gray90">
                        <PrintField label="נוצר בתאריך" value={row?.createdAt} />
                      </div>

                      {/* Assignees */}
                      <div className="px-3.5 py-2 border-b border-gray90">
                        <PrintSectionLabel
                          label="מוקצים"
                          count={row?.assignees.length}
                        />
                        {row?.assignees.length === 0 ? (
                          <span className="text-[13px] text-gray50">
                            לא הוקצה
                          </span>
                        ) : (
                          <PrintDetailTable
                            headers={['#', 'שם', 'תפקיד']}
                            rows={row?.assignees?.map((a: any, i: number) => [
                              i + 1,
                              a.name,
                              a.role,
                            ])}
                          />
                        )}
                      </div>

                      {/* Description */}
                      <div className="px-3.5 py-2 border-b border-gray90">
                        <PrintSectionLabel label="תיאור" />
                        <div className="text-[13px] whitespace-pre-wrap break-words leading-relaxed">
                          {row?.description}
                        </div>
                      </div>

                      {/* Tasks */}
                      <div className="px-3.5 py-2">
                        <PrintSectionLabel
                          label="משימות"
                          count={row?.tasks?.length}
                        />
                        {row?.tasks?.length === 0 ? (
                          <span className="text-[13px] text-gray50">
                            אין משימות
                          </span>
                        ) : (
                          <PrintDetailTable
                            headers={['#', 'כותרת משימה', 'נמענים', 'סטטוס']}
                            rows={row?.tasks?.map((t: any, i: number) => [
                              i + 1,
                              t?.title,
                              t?.recipients,
                              <img
                                key={i}
                                src={
                                  t?.isCompleted
                                    ? SVG.completedCheckboxIcon
                                    : SVG.regularCheckboxIcon
                                }
                                alt={t?.isCompleted ? 'הושלם' : 'לא הושלם'}
                                className="w-4 h-4 inline-block"
                              />,
                            ])}
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <AppMenu
            items={MENUITEMS}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onOpen={handleMenuOpen}
            onItemClick={item => handleMenuChange(item)}
            selectedItem={dailyBriefGroupBy}
            showDivider
            dividerIndex={1}
          />
          <AppConfirmationDialog
            open={showPrintConfirmationDialog}
            onClose={handleConfirmationClose}
            onConfirm={handleOnConfirm}
            icon={
              <img
                src={SVG.printerAquaLgIcon}
                alt="icon"
                className="w-[48px] h-[48px] object-contain"
              />
            }
            title="הדפסה"
            subTitle="שליחה להדפסה?"
            confirmBtnText="כן"
            cancelBtnText="לא"
          />

          <AppDialog
            showModel={showCalendar}
            showCloseIcon={false}
            onClose={() => setShowCalendar(false)}
            headerClasses="!px-0">
            <div className="flex justify-center rtl !w-full">
              <DayPicker
                locale={he}
                showOutsideDays
                mode="single"
                hideNavigation={true}
                selected={selectedDate}
                onSelect={date => {
                  if (date) setSelectedDate(getDateInUTC(date));
                }}
                components={{
                  MonthCaption: ({calendarMonth}) => (
                    <MonthCaption displayMonth={calendarMonth.date} />
                  ),
                }}
                modifiersClassNames={{
                  selected: 'rdp-selected-custom',
                  today: 'rdp-today-custom',
                  outside: 'rdp-outside-custom',
                }}
                classNames={{
                  day: 'rdp-day-custom',
                  month: 'flex flex-col gap-4 items-center w-full',
                  month_caption:
                    'flex justify-center items-center relative h-9 mb-1',
                }}
              />
            </div>
            <div className="flex justify-end pl-20 w-full gap-2 mt-8">
              <AppButton
                onClick={() => {
                  setSelectedDate(briefSelectedDate);
                  setShowCalendar(false);
                }}>
                ביטול
              </AppButton>
              <AppButton
                onClick={() => {
                  setBriefSelectedDate(selectedDate);
                  setShowCalendar(false);
                }}
                variant="primary">
                בחירה
              </AppButton>
            </div>
          </AppDialog>
        </div>
      </div>
    </div>
  );
}

export default Header;
